import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Pagination } from '../../components/ui/pagination';
import {
  Plus,
  Folder,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  FolderOpen,
  User,
  Building2,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Document, CategoryWithDocuments } from '../../lib/api';
import { getFileIcon, getCategoryColor } from './utils';

// When a single category balloons beyond this many documents, the flat
// three-column grid becomes unusable — especially when every doc comes from
// a different uploader. In that case we break the category into per-uploader
// sub-folders (Finder-style tiles, or a collapsible row list).
// The sub-folder LABELS come from the per-doc `company_name` field, which
// the backend serializer already scrubs to "Shared" when a customer viewer
// would otherwise be able to read another customer's identity — so the UI
// grouping here never leaks names.
const SUBGROUP_THRESHOLD = 20;

// Client-side pagination page-size for the drilled-in doc list.
const DOCS_PER_PAGE = 24;
// Top-level page-size — how many categories (folders) render per page.
// Each card is large so we keep the page small.
const CATEGORIES_PER_PAGE = 5;

type SubLayout = 'tiles' | 'list';

interface FolderViewProps {
  categories: CategoryWithDocuments[];
  searchTerm: string;
  onDownload: (doc: Document) => void;
  onUploadOpen: () => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  // CR-5330: Open the document details popup. When provided, clicking a
  // tile/row body opens the modal; clicking inline buttons or the kebab
  // menu still triggers their direct action (those handlers stop event
  // propagation so a row click can't fire alongside a button click).
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
}

export function FolderView({
  categories,
  searchTerm,
  onDownload,
  onUploadOpen,
  onDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  subLayout,
}: FolderViewProps) {
  // Top-level pagination across categories (folders). Renders identical
  // controls top and bottom. With only one folder visible there's nothing to
  // page through so the control stays hidden.
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPageSize, setCategoryPageSize] = useState(CATEGORIES_PER_PAGE);

  const totalCategoryPages = Math.max(1, Math.ceil(categories.length / categoryPageSize));
  const safeCategoryPage = Math.min(categoryPage, totalCategoryPages);
  const pagedCategories = useMemo(() => {
    const start = (safeCategoryPage - 1) * categoryPageSize;
    return categories.slice(start, start + categoryPageSize);
  }, [categories, safeCategoryPage, categoryPageSize]);

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No documents found matching your search' : 'No documents yet'}
            </p>
            <Button className="mt-4" onClick={onUploadOpen}>
              <Plus className="h-4 w-4 mr-2" />
              Upload your first document
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const categoryPager = totalCategoryPages > 1 ? (
    <Pagination
      currentPage={safeCategoryPage}
      totalPages={totalCategoryPages}
      totalCount={categories.length}
      itemsPerPage={categoryPageSize}
      onPageChange={setCategoryPage}
      onPageSizeChange={(n) => { setCategoryPageSize(n); setCategoryPage(1); }}
      itemLabel="folders"
    />
  ) : null;

  return (
    <div className="space-y-6">
      {categoryPager}
      {pagedCategories.map((category) => (
        <CategoryCard
          key={category.id || 'uncategorized'}
          category={category}
          onDownload={onDownload}
          onDelete={onDelete}
          onEdit={onEdit}
          onPreview={onPreview}
          onOpenDetail={onOpenDetail}
          subLayout={subLayout}
        />
      ))}
      {categoryPager}
    </div>
  );
}

interface CategoryCardProps {
  category: CategoryWithDocuments;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
}

function CategoryCard({
  category,
  onDownload,
  onDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  subLayout,
}: CategoryCardProps) {
  const shouldSubgroup = category.documents.length >= SUBGROUP_THRESHOLD;

  // Group docs by uploading company_name. Customer viewers get "Shared"
  // for any cross-customer doc (the backend scrubs the name before it
  // gets here), so those docs all fall into one safe bucket.
  const subgroups = useMemo(() => {
    if (!shouldSubgroup) return [] as Array<[string, Document[]]>;
    const m: Record<string, Document[]> = {};
    for (const d of category.documents) {
      const key = d.company_name || '(Unassigned)';
      (m[key] = m[key] || []).push(d);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [category.documents, shouldSubgroup]);

  const [drillInto, setDrillInto] = useState<string | null>(null);
  const drillDocs = drillInto
    ? (subgroups.find(([n]) => n === drillInto)?.[1] ?? [])
    : [];

  // Pagination state for the currently-visible doc list (drill target if
  // sub-grouped, otherwise the flat category doc list).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DOCS_PER_PAGE);

  const visibleDocs = shouldSubgroup && !drillInto ? [] : (shouldSubgroup ? drillDocs : category.documents);
  const totalDocs = visibleDocs.length;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedDocs = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return visibleDocs.slice(start, start + pageSize);
  }, [visibleDocs, safePage, pageSize]);

  const resetPaging = () => { setPage(1); };

  const pager = (!shouldSubgroup || drillInto) && totalPages > 1 ? (
    <Pagination
      currentPage={safePage}
      totalPages={totalPages}
      totalCount={totalDocs}
      itemsPerPage={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      itemLabel="documents"
    />
  ) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {shouldSubgroup && drillInto && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => { setDrillInto(null); resetPaging(); }}
                aria-label="Back to folders"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className={`p-2 rounded-lg ${getCategoryColor(category.color)}`}>
              {drillInto ? <Building2 className="h-5 w-5" /> : <Folder className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl truncate">
                {drillInto ? drillInto : category.name}
              </CardTitle>
              {!drillInto && category.description && (
                <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
              )}
              {drillInto && (
                <p className="text-sm text-muted-foreground mt-1">
                  {category.name} › {drillDocs.length} {drillDocs.length === 1 ? 'document' : 'documents'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {shouldSubgroup && !drillInto && (
              <Badge variant="outline" className="text-xs">
                {subgroups.length} {subgroups.length === 1 ? 'folder' : 'folders'}
              </Badge>
            )}
            <Badge variant="secondary">
              {drillInto
                ? `${drillDocs.length} ${drillDocs.length === 1 ? 'document' : 'documents'}`
                : `${category.document_count} ${category.document_count === 1 ? 'document' : 'documents'}`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {shouldSubgroup && !drillInto ? (
          subLayout === 'tiles' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {subgroups.map(([label, docs]) => (
                <CustomerTile
                  key={label}
                  label={label}
                  count={docs.length}
                  onOpen={() => { setDrillInto(label); resetPaging(); }}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {subgroups.map(([label, docs]) => (
                <CustomerRow
                  key={label}
                  label={label}
                  documents={docs}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onOpenDetail={onOpenDetail}
                  docLayout={subLayout}
                />
              ))}
            </div>
          )
        ) : (
          <>
            {pager}
            {subLayout === 'list' ? (
              <DocumentRowList
                documents={pagedDocs}
                onDownload={onDownload}
                onDelete={onDelete}
                onEdit={onEdit}
                onPreview={onPreview}
                onOpenDetail={onOpenDetail}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pagedDocs.map((doc) => (
                  <DocumentTile
                    key={doc.id}
                    doc={doc}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onPreview={onPreview}
                    onOpenDetail={onOpenDetail}
                  />
                ))}
              </div>
            )}
            {pager}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface CustomerTileProps {
  label: string;
  count: number;
  onOpen: () => void;
}

function CustomerTile({ label, count, onOpen }: CustomerTileProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      className="group flex flex-col rounded-lg border bg-card p-3 text-left transition-all duration-150 ease-out hover:border-primary/50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-center justify-between">
        <Folder className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.4} />
        <span className="text-[11px] font-semibold tabular-nums text-muted-foreground group-hover:text-foreground">
          {count}
        </span>
      </div>
      <p className="mt-3 text-[13px] font-medium leading-snug line-clamp-2 break-words">
        {label}
      </p>
    </button>
  );
}

interface CustomerRowProps {
  label: string;
  documents: Document[];
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  docLayout: SubLayout;
}

function CustomerRow({ label, documents, onDownload, onDelete, onEdit, onPreview, onOpenDetail, docLayout }: CustomerRowProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{label}</span>
        </div>
        <Badge variant="secondary" className="shrink-0">{documents.length}</Badge>
      </button>
      {expanded && (
        docLayout === 'list' ? (
          <div className="px-3 pb-3 pt-1 bg-muted/20">
            <DocumentRowList
              documents={documents}
              onDownload={onDownload}
              onDelete={onDelete}
              onEdit={onEdit}
              onPreview={onPreview}
              onOpenDetail={onOpenDetail}
            />
          </div>
        ) : (
          <div className="px-3 pb-3 pt-1 bg-muted/20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {documents.map((doc) => (
              <DocumentTile
                key={doc.id}
                doc={doc}
                onDownload={onDownload}
                onDelete={onDelete}
                onEdit={onEdit}
                onPreview={onPreview}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

interface DocumentTileProps {
  doc: Document;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
}

function DocumentTile({ doc, onDownload, onDelete, onEdit, onPreview, onOpenDetail }: DocumentTileProps) {
  const cleanDescription = stripImportMetadata(doc.description);
  // CR-5330: Tile body opens the details popup; the kebab menu still
  // exposes quick actions. stopPropagation() on the menu trigger keeps
  // the row click from firing alongside a menu item click.
  const handleBodyClick = () => onOpenDetail?.(doc);
  const handleBodyKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail?.(doc);
    }
  };
  return (
    <Card className="group hover:shadow-md transition-shadow h-full">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className={`flex items-start gap-3 flex-1 min-w-0 ${onOpenDetail ? 'cursor-pointer' : ''}`}
            onClick={onOpenDetail ? handleBodyClick : undefined}
            onKeyDown={onOpenDetail ? handleBodyKey : undefined}
            role={onOpenDetail ? 'button' : undefined}
            tabIndex={onOpenDetail ? 0 : undefined}
          >
            <div className="text-muted-foreground mt-0.5 shrink-0">{getFileIcon(doc)}</div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[13px] leading-snug line-clamp-2 break-words" title={doc.title}>
                {doc.title}
              </h3>
              {cleanDescription && (
                <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                  {cleanDescription}
                </p>
              )}
            </div>
          </div>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -mt-1 opacity-60 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownload(doc)}>
                <Download className="h-4 w-4 mr-2" />Download
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPreview?.(doc)}>
                <Eye className="h-4 w-4 mr-2" />Preview
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(doc)}>
                  <Edit className="h-4 w-4 mr-2" />Edit
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-3 border-t gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <User className="h-3 w-3 shrink-0" />
            <span className="truncate">{doc.uploaded_by_name || '—'}</span>
          </div>
          {doc.formatted_file_size && (
            <span className="shrink-0 tabular-nums">{doc.formatted_file_size}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-2">
          {doc.is_public && (
            <Badge variant="outline" className="text-[10px]">Public</Badge>
          )}
          {/* CR-5329 */}
          {doc.requires_acceptance && (
            <Badge variant="default" className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
              Requires acceptance
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface DocumentRowListProps {
  documents: Document[];
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
}

function DocumentRowList({ documents, onDownload, onDelete, onEdit, onPreview, onOpenDetail }: DocumentRowListProps) {
  return (
    <div className="divide-y border rounded-lg overflow-hidden">
      {documents.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          onDownload={onDownload}
          onDelete={onDelete}
          onEdit={onEdit}
          onPreview={onPreview}
          onOpenDetail={onOpenDetail}
        />
      ))}
    </div>
  );
}

function DocumentRow({ doc, onDownload, onDelete, onEdit, onPreview, onOpenDetail }: DocumentTileProps) {
  // CR-5330: Row body opens the details popup; the inline action
  // buttons + kebab menu stay quick actions. stopPropagation() on each
  // button keeps a button click from also bubbling into the row click.
  const handleRowClick = () => onOpenDetail?.(doc);
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 hover:bg-muted/40 transition-colors ${onOpenDetail ? 'cursor-pointer' : ''}`}
      onClick={onOpenDetail ? handleRowClick : undefined}
      role={onOpenDetail ? 'button' : undefined}
      tabIndex={onOpenDetail ? 0 : undefined}
      onKeyDown={onOpenDetail ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(doc); } } : undefined}
    >
      <div className="text-muted-foreground shrink-0">{getFileIcon(doc)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={doc.title}>
          {doc.title}
        </p>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
          {doc.uploaded_by_name && (
            <span className="flex items-center gap-1 min-w-0">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{doc.uploaded_by_name}</span>
            </span>
          )}
          {doc.formatted_file_size && (
            <span className="tabular-nums">{doc.formatted_file_size}</span>
          )}
          {doc.is_public && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">Public</Badge>
          )}
          {doc.requires_acceptance && (
            <Badge className="text-[10px] py-0 h-4 bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">Requires acceptance</Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0" onClick={stop}>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPreview?.(doc)} title="Preview">
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDownload(doc)} title="Download">
          <Download className="h-4 w-4" />
        </Button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(doc)}>
                <Edit className="h-4 w-4 mr-2" />Edit
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function stripImportMetadata(description?: string): string {
  if (!description) return '';
  const clean = description
    .replace(/\[mab-doc#[^\]]+\]/gi, '')
    .replace(/Original\s*FileName\s*:[^|]*?(?=SourceID|$)/i, '')
    .replace(/SourceID\s*:\s*\S+/i, '')
    .trim();
  return clean.length > 3 ? clean : '';
}
